import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Chip, Grid, Card, CardContent,
  FormControl, InputLabel, Select, TablePagination, Alert, Snackbar,
  InputAdornment, Tabs, Tab, CircularProgress, Tooltip, LinearProgress, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, FilterList as FilterIcon, Assessment as AssessmentIcon,
  GetApp as ExportIcon, Check as CheckIcon, AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  Upload as UploadIcon, Description as TemplateIcon,
  AttachFile as AttachFileIcon, Visibility as VisibilityIcon,
  History as HistoryIcon, AccountBalance as BalanceIcon,
  Wallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  People as PeopleIcon,
  ShowChart as ShowChartIcon,
  Repeat as RepeatIcon,
  AutorenewOutlined as AutorenewIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  ReferenceLine
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
// For file serving (receipts), use the backend server, not CloudFront
const BACKEND_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5001';

function ExpenseManagement() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentTab, setCurrentTab] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  // Carry-forward balance (balance from end of previous month)
  const [carryForward, setCarryForward] = useState(null);
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const [auditTotal, setAuditTotal] = useState(0);
  const isOwner = user?.role === 'owner';
  
  // Receipt upload states
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);
  const [receiptBlobLoading, setReceiptBlobLoading] = useState(false);
  const [receiptBlobError, setReceiptBlobError] = useState(false);

  // Duplicate warning dialog
  const [duplicateWarning, setDuplicateWarning] = useState({ open: false, duplicates: [] });

  // Petty cash state
  const [pettyCashBalance, setPettyCashBalance] = useState(null);
  const [pettyCashReceipts, setPettyCashReceipts] = useState([]);
  const [fundExpenses, setFundExpenses] = useState([]);
  const [pcLoading, setPcLoading] = useState(false);
  const [pcReceiptDialog, setPcReceiptDialog] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [pcReceiptForm, setPcReceiptForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    receivedFrom: '',
    receivedByUserId: '',
    receivedByName: '',
    transferMode: '',
    referenceNo: '',
    remarks: ''
  });
  const [pcEmployees, setPcEmployees] = useState([]);
  const [pcHolders, setPcHolders] = useState([]);

  // Financial Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsYear, setInsightsYear] = useState(new Date().getFullYear());

  // Recurring expenses state
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringPreview, setRecurringPreview] = useState([]);
  const [recurringPreviewOpen, setRecurringPreviewOpen] = useState(false);
  const [recurringGenerating, setRecurringGenerating] = useState(false);
  const [recurringTemplateForm, setRecurringTemplateForm] = useState(null); // null=closed, {}=new, obj=edit
  const [newMonthBanner, setNewMonthBanner] = useState(false);
  
  // Dynamic dropdowns from DB
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filters - Initialize with current month
  const getCurrentMonthDates = () => {
    const today = new Date();
    // Use local date values to avoid timezone issues
    const year = today.getFullYear();
    const month = today.getMonth();
    
    // First day of current month
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    
    // Last day of current month
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    
    return {
      start: firstDay,
      end: lastDayStr
    };
  };

  const currentMonth = getCurrentMonthDates();
  
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    transactionType: '',
    paymentStatus: '',
    modeOfTransaction: '',
    startDate: currentMonth.start,
    endDate: currentMonth.end,
    monthFilter: 'current' // Track which month filter is active
  });

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    particulars: '',
    category: '',
    modeOfTransaction: '',
    amount: '',
    transactionId: '',
    amountPaid: '',
    remarks: '',
    transactionType: 'Debit',
    paymentStatus: 'Paid',
    vendorName: '',
    createdBy: user?.id || '',
    paidFromFund: '',
    pettyCashHolderUserId: '',
    pettyCashHolderName: '',
    // Shoot Food extra fields
    mealType: '',
    headCount: ''
  });

  useEffect(() => {
    fetchDropdowns();
    fetchRecurringTemplates();
    checkNewMonthRecurring();
    // Load employee list for Created By filter (and petty cash dropdowns)
    api.get('/users?isActive=true').then(res => {
      const users = (res.data.data?.users || res.data.data || res.data || []).filter(u => u.isActive !== false);
      setPcEmployees(users);
    }).catch(() => {});
    if (currentTab === 0) {
      fetchExpenses();
      fetchCarryForward();
    } else if (currentTab === 1) {
      fetchAnalytics();
    } else if (currentTab === 2) {
      fetchAuditLogs();
    } else if (currentTab === 3) {
      fetchPettyCash();
    } else if (currentTab === 4) {
      // Sync year selector to the filter start date when filters change
      if (filters.startDate) {
        const yr = parseInt(filters.startDate.substring(0, 4));
        if (!isNaN(yr)) setInsightsYear(yr);
      }
      fetchInsights();
    }
  }, [page, rowsPerPage, filters, currentTab, auditPage]);

  const fetchDropdowns = async () => {
    try {
      const response = await api.get('/dropdowns');
      const dropdowns = response.data.data;
      setExpenseCategories(dropdowns.expenseCategories || []);
      setPaymentMethods(dropdowns.paymentMethods || []);
    } catch (error) {
      console.error('Dropdown fetch error:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      // Exclude monthFilter from params
      const { monthFilter, ...apiFilters } = filters;
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(apiFilters).filter(([_, v]) => v !== ''))
      });

      const response = await api.get(`/expenses?${params}`);
      setExpenses(response.data.data.expenses);
      setTotalCount(response.data.data.pagination.total);
    } catch (error) {
      showSnackbar('Failed to fetch expenses', 'error');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/expenses/analytics?${params}`);
      setAnalytics(response.data.data);
    } catch (error) {
      console.error('Analytics error:', error);
      showSnackbar('Failed to fetch analytics', 'error');
    }
  };

  // Fetch carry-forward balance: balance at end of previous month.
  // Only applicable from April 2026 onwards (March 2026 is the first month to carry forward).
  const fetchCarryForward = async () => {
    try {
      if (!filters.startDate) { setCarryForward(null); return; }
      const month = filters.startDate.substring(0, 7); // e.g. "2026-04"
      // Only show carry-forward from April 2026 onwards
      if (month < '2026-04') { setCarryForward(null); return; }
      const response = await api.get(`/expenses/carry-forward?month=${month}`);
      setCarryForward(response.data.data);
    } catch (error) {
      console.error('Carry-forward error:', error);
      setCarryForward(null);
    }
  };

  // Fetch all audit logs
  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({
        page: auditPage + 1,
        limit: 50
      });
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const response = await api.get(`/expenses/audit-logs?${params}`);
      setAuditLogs(response.data.data.logs);
      setAuditTotal(response.data.data.pagination.total);
    } catch (error) {
      console.error('Audit logs error:', error);
      showSnackbar('Failed to fetch audit logs', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  // ── PETTY CASH ──────────────────────────────────────────────────────────────
  const fetchPettyCash = async () => {
    setPcLoading(true);
    // Load employees for holder/receiver dropdowns
    if (pcEmployees.length === 0) {
      api.get('/users?isActive=true').then(res => {
        const users = (res.data.data?.users || res.data.data || res.data || []).filter(u => u.employeeId || u.isActive);
        setPcEmployees(users);
      }).catch(() => {});
    }
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const [balRes, rcpRes, expRes] = await Promise.all([
        api.get('/petty-cash/balance'),  // always all-time — no date filter
        api.get('/petty-cash/receipts', { params }),
        api.get('/petty-cash/fund-expenses', { params })
      ]);
      setPettyCashBalance(balRes.data.data);
      setPettyCashReceipts(rcpRes.data.data.receipts);
      setFundExpenses(expRes.data.data.expenses);
    } catch (error) {
      showSnackbar('Failed to fetch petty cash data', 'error');
    } finally {
      setPcLoading(false);
    }
  };

  const handleOpenReceiptDialog = (receipt = null) => {
    if (receipt) {
      setEditingReceipt(receipt);
      setPcReceiptForm({
        date: receipt.date,
        amount: receipt.amount,
        receivedFrom: receipt.receivedFrom,
        receivedByUserId: receipt.receivedByUserId || '',
        receivedByName: receipt.receivedByName || '',
        transferMode: receipt.transferMode,
        referenceNo: receipt.referenceNo || '',
        remarks: receipt.remarks || ''
      });
    } else {
      setEditingReceipt(null);
      setPcReceiptForm({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        receivedFrom: '',
        receivedByUserId: '',
        receivedByName: '',
        transferMode: '',
        referenceNo: '',
        remarks: ''
      });
    }
    setPcReceiptDialog(true);
  };

  const handleSaveReceipt = async () => {
    try {
      const payload = {
        ...pcReceiptForm,
        amount: parseFloat(pcReceiptForm.amount),
        createdBy: user?.id
      };
      if (editingReceipt) {
        await api.put(`/petty-cash/receipts/${editingReceipt.id}`, payload);
        showSnackbar('Receipt updated successfully');
      } else {
        await api.post('/petty-cash/receipts', payload);
        showSnackbar('Receipt recorded successfully');
      }
      setPcReceiptDialog(false);
      fetchPettyCash();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save receipt', 'error');
    }
  };

  const handleDeleteReceipt2 = async (id) => {
    if (!window.confirm('Delete this petty cash receipt?')) return;
    try {
      await api.delete(`/petty-cash/receipts/${id}`);
      showSnackbar('Receipt deleted');
      fetchPettyCash();
    } catch (error) {
      showSnackbar('Failed to delete receipt', 'error');
    }
  };

  // ── RECURRING EXPENSES ────────────────────────────────────────────────────
  const fetchRecurringTemplates = async () => {
    try {
      const res = await api.get('/expenses/recurring');
      setRecurringTemplates(res.data.data || []);
    } catch (e) { /* silent */ }
  };

  const checkNewMonthRecurring = async () => {
    const key = 'recurringLastCheckedMonth';
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastChecked = localStorage.getItem(key);
    if (lastChecked === thisMonth) return; // already checked this month
    // Preview to see if there's anything to generate
    try {
      const res = await api.get('/expenses/recurring/preview');
      const pending = (res.data.data || []).filter(i => !i.alreadyExists);
      if (pending.length > 0) {
        setRecurringPreview(res.data.data || []);
        setNewMonthBanner(true);
      }
      localStorage.setItem(key, thisMonth);
    } catch (e) { /* silent */ }
  };

  const handleGenerateRecurring = async () => {
    setRecurringGenerating(true);
    try {
      const res = await api.post('/expenses/recurring/generate', { userId: user?.id });
      const { created, skipped } = res.data.data;
      showSnackbar(`✅ ${created.length} recurring expense(s) added. ${skipped.length} already existed.`, 'success');
      setRecurringPreviewOpen(false);
      setNewMonthBanner(false);
      fetchExpenses();
      fetchInsights();
    } catch (e) {
      showSnackbar('Failed to generate recurring expenses', 'error');
    } finally {
      setRecurringGenerating(false);
    }
  };

  const handleOpenRecurringPreview = async () => {
    try {
      const res = await api.get('/expenses/recurring/preview');
      setRecurringPreview(res.data.data || []);
      setRecurringPreviewOpen(true);
    } catch (e) { showSnackbar('Failed to load preview', 'error'); }
  };

  const handleSaveTemplate = async (formValues) => {
    try {
      if (formValues.id) {
        await api.put(`/expenses/recurring/${formValues.id}`, formValues);
        showSnackbar('Template updated', 'success');
      } else {
        await api.post('/expenses/recurring', formValues);
        showSnackbar('Template added', 'success');
      }
      fetchRecurringTemplates();
      setRecurringTemplateForm(null);
    } catch (e) { showSnackbar('Failed to save template', 'error'); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`/expenses/recurring/${id}`);
      showSnackbar('Template deleted', 'success');
      fetchRecurringTemplates();
    } catch (e) { showSnackbar('Failed to delete template', 'error'); }
  };

  // ── FINANCIAL INSIGHTS ────────────────────────────────────────────────────
  const fetchInsights = async (year = insightsYear) => {
    setInsightsLoading(true);
    try {
      const params = new URLSearchParams({ year });
      // Pass the main filter dates if set — they override the year range
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate)   params.set('endDate',   filters.endDate);
      const res = await api.get(`/expenses/financial-insights?${params.toString()}`);
      setInsights(res.data.data);
    } catch (e) {
      showSnackbar('Failed to load financial insights', 'error');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleOpenDialog = (expense = null) => {
    // Load employees for petty cash receipt "Received By" dropdown
    if (pcEmployees.length === 0) {
      api.get('/users?isActive=true').then(res => {
        const users = (res.data.data?.users || res.data.data || res.data || []).filter(u => u.isActive !== false);
        setPcEmployees(users);
      }).catch(() => {});
    }
    // Load petty cash holders (only employees who have actually received petty cash)
    api.get('/petty-cash/holders').then(res => {
      setPcHolders(res.data.data?.holders || []);
    }).catch(() => {});
    if (expense) {
      setEditingExpense(expense);
      // Parse mealType/headCount from remarks if Shoot Food
      let mealType = '', headCount = '';
      if (expense.category === 'Shoot Food' && expense.remarks) {
        const mtMatch = expense.remarks.match(/Meal:\s*(\w+)/i);
        const hcMatch = expense.remarks.match(/Pax:\s*(\d+)/i);
        if (mtMatch) mealType = mtMatch[1];
        if (hcMatch) headCount = hcMatch[1];
      }
      setFormData({
        date: expense.date,
        particulars: expense.particulars,
        category: expense.category,
        modeOfTransaction: expense.modeOfTransaction,
        amount: expense.amount,
        transactionId: expense.transactionId || '',
        amountPaid: expense.amountPaid || expense.amount,
        remarks: expense.remarks || '',
        transactionType: expense.transactionType,
        paymentStatus: expense.paymentStatus,
        vendorName: expense.vendorName || '',
        createdBy: user?.id || '',
        paidFromFund: expense.paidFromFund || '',
        pettyCashHolderUserId: expense.pettyCashHolderUserId || '',
        pettyCashHolderName: expense.pettyCashHolderName || '',
        mealType,
        headCount
      });
    } else {
      setEditingExpense(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        particulars: '',
        category: '',
        modeOfTransaction: '',
        amount: '',
        transactionId: '',
        amountPaid: '',
        remarks: '',
        transactionType: 'Debit',
        paymentStatus: 'Paid',
        vendorName: '',
        createdBy: user?.id || '',
        paidFromFund: '',
        pettyCashHolderUserId: '',
        pettyCashHolderName: '',
        mealType: '',
        headCount: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
    setReceiptFile(null);
  };

  const handleViewReceipt = async (expense) => {
    setViewingReceipt(expense);
    setReceiptViewerOpen(true);
    setReceiptBlobUrl(null);
    setReceiptBlobError(false);
    setReceiptBlobLoading(true);
    try {
      const filename = expense.receiptUrl.split('/').pop();
      const res = await api.get(`/expenses/receipt-file/${filename}`);
      setReceiptBlobUrl(res.data.url); // presigned URL
    } catch (_) {
      setReceiptBlobError(true);
    } finally {
      setReceiptBlobLoading(false);
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) {
      return;
    }
    
    try {
      await api.delete(`/expenses/${expenseId}/receipt`);
      showSnackbar('Receipt deleted successfully');
      setReceiptViewerOpen(false);
      fetchExpenses();
    } catch (error) {
      showSnackbar('Failed to delete receipt', 'error');
    }
  };

  const doSubmit = async () => {
    try {
      // For Shoot Food: encode meal type and head count into remarks
      let finalRemarks = formData.remarks || '';
      if (formData.category === 'Shoot Food') {
        if (!formData.mealType) { showSnackbar('Please select Breakfast or Dinner for Shoot Food', 'error'); return; }
        if (!formData.headCount || parseInt(formData.headCount) < 1) { showSnackbar('Please enter number of people for Shoot Food', 'error'); return; }
        const shootFoodTag = `Meal: ${formData.mealType} | Pax: ${formData.headCount}`;
        // Replace existing tag or prepend
        finalRemarks = finalRemarks.replace(/Meal:\s*\w+\s*\|\s*Pax:\s*\d+/i, '').trim();
        finalRemarks = finalRemarks ? `${shootFoodTag} | ${finalRemarks}` : shootFoodTag;
      }
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        amountPaid: formData.amountPaid ? parseFloat(formData.amountPaid) : parseFloat(formData.amount),
        remarks: finalRemarks
      };

      let expenseId;
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, submitData);
        expenseId = editingExpense.id;
        showSnackbar('Expense updated successfully');
      } else {
        const response = await api.post('/expenses', submitData);
        expenseId = response.data.data.expense.id;
        showSnackbar('Expense created successfully');
      }

      // Upload receipt if provided
      if (receiptFile && expenseId) {
        const formData = new FormData();
        formData.append('receipt', receiptFile);
        try {
          await api.post(`/expenses/${expenseId}/receipt`, formData, {
            headers: { 'Content-Type': undefined } // let axios set multipart/form-data with correct boundary
          });
        } catch (error) {
          console.error('Receipt upload error:', error.response?.data || error.message);
          showSnackbar('Expense saved but receipt upload failed', 'warning');
        }
      }

      handleCloseDialog();
      fetchExpenses();
      if (currentTab === 1) fetchAnalytics();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save expense', 'error');
    }
  };

  const handleSubmit = async () => {
    // Duplicate check only for new expenses (not edits)
    if (!editingExpense && formData.date && formData.amount && formData.category) {
      try {
        const res = await api.get('/expenses/check-duplicate', {
          params: { date: formData.date, amount: formData.amount, category: formData.category }
        });
        const { isDuplicate, duplicates } = res.data.data;
        if (isDuplicate) {
          setDuplicateWarning({ open: true, duplicates });
          return;
        }
      } catch (e) {
        // Silently ignore check errors — don't block creation
      }
    }
    await doSubmit();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await api.delete(`/expenses/${id}`);
        showSnackbar('Expense deleted successfully');
        fetchExpenses();
        if (currentTab === 1) fetchAnalytics();
      } catch (error) {
        showSnackbar('Failed to delete expense', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const exportToCSV = () => {
    const formatDateForCSV = (dateStr) => {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const csvContent = [
      ['Date', 'Particulars', 'Category', 'Mode of Transaction', 'Amount', 'Transaction ID', 'Amount Paid', 'Remarks', 'Debit/Credit'].join(','),
      ...expenses.map(exp => [
        formatDateForCSV(exp.date),
        `"${exp.particulars}"`,
        exp.category,
        exp.modeOfTransaction,
        exp.amount,
        exp.transactionId || '',
        exp.amountPaid || exp.amount,
        `"${exp.remarks || ''}"`,
        exp.transactionType
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Smart normalization functions
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Try DD/MM/YYYY or D/M/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const d = day.padStart(2, '0');
        const m = month.padStart(2, '0');
        const y = year.length === 2 ? '20' + year : year;
        return `${y}-${m}-${d}`;
      }
    }
    
    // Try YYYY-MM-DD format
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return dateStr; // Already in correct format
      }
    }
    
    // Try DD-MM-YYYY format
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length <= 2) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    
    return null;
  };

  const normalizeCategory = (inputCategory) => {
    if (!inputCategory) return null;
    
    const normalized = inputCategory.trim().toLowerCase();
    
    // Find best match from available categories
    const exactMatch = expenseCategories.find(cat => cat.toLowerCase() === normalized);
    if (exactMatch) return exactMatch;
    
    // Try partial match
    const partialMatch = expenseCategories.find(cat => 
      cat.toLowerCase().includes(normalized) || normalized.includes(cat.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    // Return original if no match (will be validated by API)
    return inputCategory.trim();
  };

  const normalizePaymentMode = (inputMode) => {
    if (!inputMode) return null;
    
    const normalized = inputMode.trim().toLowerCase();
    
    // Common aliases
    const aliases = {
      'cash': ['cash', 'money', 'notes'],
      'g-pay': ['gpay', 'g-pay', 'g pay', 'google pay', 'googlepay'],
      'phone pay': ['phonepe', 'phone pay', 'phone-pay', 'phonepay'],
      'paytm': ['paytm', 'pay tm'],
      'bank transfer': ['bank', 'neft', 'imps', 'rtgs', 'transfer', 'bank transfer', 'banktransfer'],
      'upi': ['upi', 'unified payment'],
      'credit card': ['credit', 'credit card', 'creditcard', 'cc'],
      'debit card': ['debit', 'debit card', 'debitcard', 'dc']
    };
    
    // Try to find match in aliases
    for (const [standard, variations] of Object.entries(aliases)) {
      if (variations.some(v => normalized === v || normalized.includes(v))) {
        // Find the exact match in paymentMethods
        const exactMatch = paymentMethods.find(method => method.toLowerCase() === standard);
        if (exactMatch) return exactMatch;
      }
    }
    
    // Try exact match
    const exactMatch = paymentMethods.find(method => method.toLowerCase() === normalized);
    if (exactMatch) return exactMatch;
    
    // Try partial match
    const partialMatch = paymentMethods.find(method => 
      method.toLowerCase().includes(normalized) || normalized.includes(method.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    // Return original if no match (will be validated by API)
    return inputMode.trim();
  };

  const handleFileUpload = async (file) => {
    try {
      setLoading(true);
      
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        showSnackbar('File is empty or invalid', 'error');
        return;
      }
      
      // Skip header row
      const dataLines = lines.slice(1);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      const warnings = [];
      
      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        try {
          // Parse CSV line handling quoted fields
          const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
          const cleanValues = values.map(v => v.replace(/^"|"$/g, '').trim());
          
          if (cleanValues.length < 5) {
            errorCount++;
            errors.push(`Row ${i + 2}: Insufficient data`);
            continue;
          }
          
          const [dateStr, particulars, category, modeOfTransaction, amount, transactionId, amountPaid, remarks, transactionType] = cleanValues;
          
          if (!dateStr || !particulars || !category || !modeOfTransaction || !amount) {
            errorCount++;
            errors.push(`Row ${i + 2}: Missing required fields`);
            continue;
          }
          
          // Smart date normalization
          const formattedDate = normalizeDate(dateStr);
          if (!formattedDate) {
            errorCount++;
            errors.push(`Row ${i + 2}: Invalid date format '${dateStr}'`);
            continue;
          }
          
          // Smart category normalization
          const normalizedCategory = normalizeCategory(category);
          if (normalizedCategory !== category) {
            warnings.push(`Row ${i + 2}: Category '${category}' normalized to '${normalizedCategory}'`);
          }
          
          // Smart payment mode normalization
          const normalizedMode = normalizePaymentMode(modeOfTransaction);
          if (normalizedMode !== modeOfTransaction) {
            warnings.push(`Row ${i + 2}: Mode '${modeOfTransaction}' normalized to '${normalizedMode}'`);
          }
          
          const expense = {
            date: formattedDate,
            particulars: particulars,
            category: normalizedCategory,
            modeOfTransaction: normalizedMode,
            amount: parseFloat(amount),
            transactionId: transactionId || '',
            amountPaid: amountPaid ? parseFloat(amountPaid) : parseFloat(amount),
            remarks: remarks || '',
            transactionType: transactionType || 'Debit',
            paymentStatus: 'Paid',
            createdBy: user?.id
          };
          
          // Create expense via API
          await api.post('/expenses', expense);
          successCount++;
        } catch (error) {
          console.error('Error processing line:', error);
          errorCount++;
          errors.push(`Row ${i + 2}: ${error.response?.data?.message || error.message}`);
        }
      }
      
      if (successCount > 0) {
        const msg = `Successfully uploaded ${successCount} expenses${errorCount > 0 ? `, ${errorCount} failed` : ''}${warnings.length > 0 ? `. ${warnings.length} values auto-corrected` : ''}`;
        showSnackbar(msg, 'success');
        console.log('Upload warnings:', warnings);
        fetchExpenses();
        if (currentTab === 1) fetchAnalytics();
      } else {
        const errorMsg = errors.slice(0, 3).join('; ');
        showSnackbar(`Failed to upload expenses. ${errorMsg}`, 'error');
      }
      
    } catch (error) {
      console.error('File upload error:', error);
      showSnackbar('Error uploading file: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          💰 Expense Management
        </Typography>
        <Box display="flex" gap={1}>
          {/* Recurring Expenses button */}
          <Tooltip title="Recurring Expenses">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RepeatIcon />}
              onClick={() => { setRecurringDialogOpen(true); handleOpenRecurringPreview(); }}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Recurring
            </Button>
          </Tooltip>
          <Tooltip title="Recurring Expenses">
            <IconButton
              color="secondary"
              onClick={() => { setRecurringDialogOpen(true); handleOpenRecurringPreview(); }}
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, border: '1px solid', borderColor: 'secondary.main' }}
            >
              <RepeatIcon />
            </IconButton>
          </Tooltip>

          {/* Export button */}
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportToCSV}
            sx={{ 
              display: { xs: 'none', sm: 'inline-flex' }
            }}
          >
            Export
          </Button>
          <IconButton
            color="primary"
            onClick={exportToCSV}
            sx={{ 
              display: { xs: 'inline-flex', sm: 'none' },
              border: '1px solid',
              borderColor: 'primary.main'
            }}
          >
            <ExportIcon />
          </IconButton>

          {/* Add Expense button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              display: { xs: 'none', sm: 'inline-flex' }
            }}
          >
            Add
          </Button>
          <IconButton
            color="primary"
            onClick={() => handleOpenDialog()}
            sx={{ 
              display: { xs: 'inline-flex', sm: 'none' },
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
        <Tab label="📋 Expenses List" />
        <Tab label="📊 Analytics & Reports" />
        <Tab label="🔍 Audit History" />
        <Tab label="💵 Petty Cash" />
        {isOwner && <Tab label="💡 Financial Insights" />}
      </Tabs>

      {/* Filters - Always visible for both tabs */}
      <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Monthly Filter */}
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Month Filter"
                  value={filters.monthFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = today.getMonth();
                    let newStartDate = '';
                    let newEndDate = '';
                    
                    if (value === 'current') {
                      // Current month: 1st to last day
                      newStartDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
                      const lastDay = new Date(year, month + 1, 0).getDate();
                      newEndDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                    } else if (value === 'last') {
                      // Last month: 1st to last day
                      const lastMonthYear = month === 0 ? year - 1 : year;
                      const lastMonth = month === 0 ? 11 : month - 1;
                      newStartDate = `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`;
                      const lastDay = new Date(lastMonthYear, lastMonth + 1, 0).getDate();
                      newEndDate = `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                    } else if (value === 'all') {
                      newStartDate = '';
                      newEndDate = '';
                    }
                    
                    setFilters({ 
                      ...filters, 
                      monthFilter: value,
                      startDate: newStartDate,
                      endDate: newEndDate
                    });
                  }}
                >
                  <MenuItem value="current">Current Month</MenuItem>
                  <MenuItem value="last">Last Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </TextField>
              </Grid>

              {/* Search */}
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  }}
                />
              </Grid>

              {/* Category Filter */}
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Category"
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  {expenseCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Type Filter */}
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth size="small" select
                  label="Status"
                  value={filters.paymentStatus}
                  onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Type"
                  value={filters.transactionType}
                  onChange={(e) => setFilters({ ...filters, transactionType: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Debit">Debit</MenuItem>
                  <MenuItem value="Credit">Credit</MenuItem>
                </TextField>
              </Grid>

              {/* Vendor filter */}
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth size="small"
                  label="Vendor"
                  value={filters.vendorName || ''}
                  onChange={e => setFilters({ ...filters, vendorName: e.target.value })}
                  placeholder="Filter by vendor…"
                />
              </Grid>

              {/* Created By filter — owner only */}
              {user?.role === 'owner' && (
                <Grid item xs={12} md={1.5}>
                  <TextField
                    fullWidth size="small" select
                    label="Created By"
                    value={filters.createdBy || ''}
                    onChange={e => setFilters({ ...filters, createdBy: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    {pcEmployees.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.fullName || u.username}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Start Date */}
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value, monthFilter: 'custom' })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value, monthFilter: 'custom' })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Reset Button */}
              <Grid item xs={12} md={0.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const currentMonth = getCurrentMonthDates();
                    setFilters({
                      search: '',
                      category: '',
                      transactionType: '',
                      paymentStatus: '',
                      modeOfTransaction: '',
                      vendorName: '',
                      createdBy: '',
                      startDate: currentMonth.start,
                      endDate: currentMonth.end,
                      monthFilter: 'current'
                    });
                  }}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
      </Paper>

      {currentTab === 0 && (
        <>
          {/* Expenses Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Particulars</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Mode</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }} align="center">📎</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Carry-Forward Balance Row */}
                {!loading && carryForward && carryForward.balance !== 0 && isOwner && (
                  <TableRow sx={{ bgcolor: carryForward.balance >= 0 ? '#e8f5e9' : '#fce4ec', borderLeft: '4px solid', borderColor: carryForward.balance >= 0 ? 'success.main' : 'error.main' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.8rem' }}>
                      {/* Show 1st of current month as opening balance date */}
                      {carryForward.openingDate
                        ? new Date(carryForward.openingDate + 'T00:00:00').toLocaleDateString('en-GB')
                        : new Date(carryForward.prevMonthEnd + 'T00:00:00').toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" fontWeight={700} color="text.primary">
                        🏦 Balance from {carryForward.prevMonthLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Carried forward from previous month
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={carryForward.balance >= 0 ? 'success.main' : 'error.main'}
                      >
                        {carryForward.balance >= 0 ? '+' : ''}{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(carryForward.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip label={carryForward.balance >= 0 ? 'Credit B/F' : 'Debit B/F'} size="small" color={carryForward.balance >= 0 ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                  </TableRow>
                )}
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      No expenses found
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((expense) => (
                    <TableRow 
                      key={expense.id} 
                      hover 
                      onClick={() => {
                        setSelectedExpense(expense);
                        setDetailsDialog(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{new Date(expense.date).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {expense.particulars}
                        </Typography>
                        {expense.vendorName && (
                          <Typography variant="caption" color="text.secondary">
                            Vendor: {expense.vendorName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip label={expense.category} size="small" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{expense.modeOfTransaction}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={expense.transactionType === 'Debit' ? 'error.main' : 'success.main'}
                        >
                          {formatCurrency(expense.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={expense.transactionType}
                          size="small"
                          color={expense.transactionType === 'Debit' ? 'error' : 'success'}
                          icon={expense.transactionType === 'Debit' ? <TrendingDownIcon /> : <TrendingUpIcon />}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={expense.paymentStatus}
                          size="small"
                          color={
                            expense.paymentStatus === 'Paid' ? 'success' :
                            expense.paymentStatus === 'Approved' ? 'info' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {expense.receiptUrl ? (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReceipt(expense);
                            }}
                            title="View Receipt"
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {expense.paymentStatus === 'Approved' && (
                          <Tooltip title="Mark as Paid">
                            <IconButton size="small" color="success"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await api.put(`/expenses/${expense.id}/mark-paid`);
                                fetchExpenses();
                              }}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(expense); }} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </>
      )}

      {currentTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon /> Expense Audit History
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Complete log of all expense changes — who created, edited, deleted or approved each entry.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date/Time</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Changed By</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Expense Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Particulars</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Fields Changed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(log.createdAt).toLocaleString('en-GB')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color={
                            log.action === 'CREATE' ? 'success' :
                            log.action === 'DELETE' ? 'error' :
                            log.action === 'APPROVE' ? 'info' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.changedByName || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {log.expenseDate ? new Date(log.expenseDate).toLocaleDateString('en-GB') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.expenseParticulars || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {log.expenseAmount ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(log.expenseAmount) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.changedFields && log.changedFields.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {log.changedFields.slice(0, 3).map(f => (
                              <Chip key={f} label={f} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            ))}
                            {log.changedFields.length > 3 && (
                              <Chip label={`+${log.changedFields.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={auditTotal}
              page={auditPage}
              onPageChange={(e, newPage) => setAuditPage(newPage)}
              rowsPerPage={50}
              rowsPerPageOptions={[50]}
            />
          </TableContainer>
        </Box>
      )}

      {currentTab === 1 && (
        <Box>
          {analytics && (
          <>
            <Grid container spacing={3} mb={3}>
            {/* Opening Balance (carry-forward) — only shown for April 2026+ */}
            {isOwner && analytics.summary.openingBalance !== 0 && analytics.summary.openingBalanceLabel && (
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid', borderColor: analytics.summary.openingBalance >= 0 ? 'success.main' : 'error.main', bgcolor: analytics.summary.openingBalance >= 0 ? '#f1f8e9' : '#fce4ec' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      🏦 Opening Balance ({analytics.summary.openingBalanceLabel})
                    </Typography>
                    <Typography variant="h4" color={analytics.summary.openingBalance >= 0 ? 'success.main' : 'error.main'}>
                      {analytics.summary.openingBalance >= 0 ? '+' : ''}{formatCurrency(analytics.summary.openingBalance)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={isOwner ? 3 : 6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Debit (Expenses)</Typography>
                  <Typography variant="h4" color="error.main">
                    {formatCurrency(analytics.summary.totalDebit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {isOwner && (
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>Total Credit (Income)</Typography>
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(analytics.summary.totalCredit)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {isOwner && (
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid', borderColor: analytics.summary.balance >= 0 ? 'success.main' : 'error.main' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Closing Balance {analytics.summary.openingBalanceLabel ? '(incl. B/F)' : ''}
                    </Typography>
                    <Typography
                      variant="h4"
                      color={analytics.summary.balance >= 0 ? 'success.main' : 'error.main'}
                    >
                      {formatCurrency(analytics.summary.balance)}
                    </Typography>
                    {analytics.summary.openingBalance !== 0 && (
                      <Typography variant="caption" color="text.secondary">
                        B/F {formatCurrency(analytics.summary.openingBalance)} + This month {formatCurrency(analytics.summary.currentMonthBalance)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={isOwner ? 3 : 6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Net Expense (This Month)</Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.summary.netExpense)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Category Breakdown</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.categoryBreakdown
                          .filter(item => isOwner || item.transactionType === 'Debit')
                          .map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.category}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.transactionType}
                                  size="small"
                                  color={item.transactionType === 'Debit' ? 'error' : 'success'}
                                />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                              <TableCell align="right">{item.count}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Payment Method Breakdown</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Payment Method</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.paymentMethodBreakdown.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.modeOfTransaction}</TableCell>
                            <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                            <TableCell align="right">{item.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
          )}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Transaction Type"
                value={formData.transactionType}
                onChange={(e) => setFormData({ ...formData, transactionType: e.target.value })}
                required
              >
                <MenuItem value="Debit">Debit (Expense)</MenuItem>
                {isOwner && <MenuItem value="Credit">Credit (Income)</MenuItem>}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Particulars"
                value={formData.particulars}
                onChange={(e) => setFormData({ ...formData, particulars: e.target.value })}
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value, mealType: '', headCount: '' })}
                required
              >
                {expenseCategories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Shoot Food — mandatory meal type and head count */}
            {formData.category === 'Shoot Food' && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Meal Type *"
                    value={formData.mealType}
                    onChange={(e) => setFormData({ ...formData, mealType: e.target.value })}
                    required
                    error={!formData.mealType}
                    helperText={!formData.mealType ? 'Required for Shoot Food' : ''}
                  >
                    <MenuItem value="Breakfast">Breakfast</MenuItem>
                    <MenuItem value="Lunch">Lunch</MenuItem>
                    <MenuItem value="Dinner">Dinner</MenuItem>
                    <MenuItem value="Snacks">Snacks</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of People *"
                    value={formData.headCount}
                    onChange={(e) => setFormData({ ...formData, headCount: e.target.value })}
                    required
                    error={!formData.headCount || parseInt(formData.headCount) < 1}
                    helperText={!formData.headCount ? 'Required for Shoot Food' : ''}
                    inputProps={{ min: 1 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">pax</InputAdornment> }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Mode of Transaction"
                value={formData.modeOfTransaction}
                onChange={(e) => setFormData({ ...formData, modeOfTransaction: e.target.value })}
                required
              >
                {paymentMethods.map(method => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Transaction ID"
                value={formData.transactionId}
                onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Payment Status"
                value={formData.paymentStatus}
                onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
              >
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Partial">Partial</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vendor/Payee Name"
                value={formData.vendorName}
                onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount Paid"
                value={formData.amountPaid}
                onChange={(e) => setFormData({ ...formData, amountPaid: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                helperText="For partial payments"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
              >
                {receiptFile ? receiptFile.name : 'Upload Receipt (Optional)'}
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      if (file.size > 5 * 1024 * 1024) {
                        showSnackbar('File size must be less than 5MB', 'error');
                        return;
                      }
                      setReceiptFile(file);
                    }
                  }}
                />
              </Button>
              {receiptFile && (
                <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {(receiptFile.size / 1024).toFixed(2)} KB
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setReceiptFile(null)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {editingExpense?.receiptUrl && !receiptFile && (
                <Box mt={1}>
                  <Typography variant="caption" color="success.main">
                    ✓ Receipt uploaded
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, border: '1px solid', borderColor: formData.paidFromFund ? 'primary.main' : 'divider', borderRadius: 1, cursor: 'pointer', bgcolor: formData.paidFromFund ? 'primary.50' : 'transparent' }}
                onClick={() => setFormData({ ...formData, paidFromFund: formData.paidFromFund ? '' : 'Petty Cash', pettyCashHolderUserId: '', pettyCashHolderName: '' })}
              >
                <input
                  type="checkbox"
                  checked={!!formData.paidFromFund}
                  readOnly
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <Typography variant="body2" fontWeight={formData.paidFromFund ? 600 : 400}>
                  Paid from Petty Cash Fund
                </Typography>
                {formData.paidFromFund && (
                  <Chip label="Petty Cash" size="small" color="primary" sx={{ ml: 'auto' }} />
                )}
              </Box>
            </Grid>
            {formData.paidFromFund && (
              <Grid item xs={12}>
                <FormControl size="small" fullWidth required>
                  <InputLabel>Whose Petty Cash?</InputLabel>
                  <Select
                    value={formData.pettyCashHolderUserId || ''}
                    label="Whose Petty Cash?"
                    onChange={e => {
                      const holder = pcHolders.find(h => h.userId === e.target.value);
                      setFormData({ ...formData, pettyCashHolderUserId: e.target.value, pettyCashHolderName: holder?.name || '' });
                    }}
                  >
                    <MenuItem value=""><em>— Select holder —</em></MenuItem>
                    {pcHolders.length === 0
                      ? <MenuItem disabled><em>No petty cash holders found — add receipts first</em></MenuItem>
                      : pcHolders.map(h => (
                        <MenuItem key={h.userId} value={h.userId}>{h.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingExpense ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog open={duplicateWarning.open} onClose={() => setDuplicateWarning({ open: false, duplicates: [] })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          ⚠️ Possible Duplicate Expense
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A similar expense already exists with the same date, category, and amount.
          </Alert>
          <Typography variant="body2" color="text.secondary" mb={1}>Existing record(s):</Typography>
          {duplicateWarning.duplicates.map((d) => (
            <Box key={d.id} sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 1.5, mb: 1, bgcolor: 'warning.50' }}>
              <Typography variant="body2"><strong>{d.date}</strong> — {d.category} — ₹{parseFloat(d.amount).toLocaleString('en-IN')}</Typography>
              <Typography variant="caption" color="text.secondary">{d.particulars}</Typography>
            </Box>
          ))}
          <Typography variant="body2" mt={2}>Do you still want to create this expense?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateWarning({ open: false, duplicates: [] })}>Go Back</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setDuplicateWarning({ open: false, duplicates: [] }); doSubmit(); }}
          >
            Create Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expense Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Expense Details</Typography>
            <Box>
              <IconButton size="small" onClick={() => { setDetailsDialog(false); handleOpenDialog(selectedExpense); }} color="primary">
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => setDetailsDialog(false)}>
                ✕
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedExpense && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {new Date(selectedExpense.date).toLocaleDateString('en-GB')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Transaction Type</Typography>
                <Box mt={0.5}>
                  <Chip 
                    label={selectedExpense.transactionType} 
                    size="small"
                    color={selectedExpense.transactionType === 'Debit' ? 'error' : 'success'}
                    icon={selectedExpense.transactionType === 'Debit' ? <TrendingDownIcon /> : <TrendingUpIcon />}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Particulars</Typography>
                <Typography variant="body1">{selectedExpense.particulars}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Box mt={0.5}>
                  <Chip label={selectedExpense.category} size="small" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                <Typography variant="body1">{selectedExpense.modeOfTransaction}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Amount</Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  color={selectedExpense.transactionType === 'Debit' ? 'error.main' : 'success.main'}
                >
                  {formatCurrency(selectedExpense.amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                <Box mt={0.5}>
                  <Chip 
                    label={selectedExpense.paymentStatus} 
                    size="small"
                    color={selectedExpense.paymentStatus === 'Paid' ? 'success' : selectedExpense.paymentStatus === 'Approved' ? 'info' : 'warning'}
                  />
                </Box>
              </Grid>
              {selectedExpense.transactionId && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                  <Typography variant="body1">{selectedExpense.transactionId}</Typography>
                </Grid>
              )}
              {selectedExpense.vendorName && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Vendor/Payee</Typography>
                  <Typography variant="body1">{selectedExpense.vendorName}</Typography>
                </Grid>
              )}
              {selectedExpense.amountPaid && selectedExpense.amountPaid !== selectedExpense.amount && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                  <Typography variant="body1">{formatCurrency(selectedExpense.amountPaid)}</Typography>
                </Grid>
              )}
              {selectedExpense.remarks && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Remarks</Typography>
                  <Typography variant="body1">{selectedExpense.remarks}</Typography>
                </Grid>
              )}
              {selectedExpense.creator && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Created By</Typography>
                  <Typography variant="body2">{selectedExpense.creator.fullName || selectedExpense.creator.username}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(selectedExpense.createdAt).toLocaleString('en-GB')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          <Button onClick={() => { setDetailsDialog(false); handleOpenDialog(selectedExpense); }} variant="contained">
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog 
        open={receiptViewerOpen} 
        onClose={() => { setReceiptViewerOpen(false); setReceiptBlobUrl(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Receipt/Invoice</Typography>
            <Box>
              <IconButton
                size="small"
                onClick={() => receiptBlobUrl && window.open(receiptBlobUrl, '_blank')}
                disabled={!receiptBlobUrl}
                title="Download"
              >
                <ExportIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteReceipt(viewingReceipt?.id)}
                color="error"
                title="Delete Receipt"
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setReceiptViewerOpen(false)}
              >
                ✕
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingReceipt?.receiptUrl && (
            <Box sx={{ width: '100%', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {receiptBlobLoading ? (
                <CircularProgress />
              ) : receiptBlobError ? (
                <Box textAlign="center" color="text.secondary" py={4}>
                  <Typography variant="body2">Receipt file not found on server.</Typography>
                  <Typography variant="caption">The file may have been lost. Please re-upload.</Typography>
                </Box>
              ) : receiptBlobUrl ? (
                viewingReceipt.receiptUrl.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={receiptBlobUrl}
                    width="100%"
                    height="600px"
                    title="Receipt PDF"
                    style={{ border: 'none' }}
                  />
                ) : (
                  <img
                    src={receiptBlobUrl}
                    alt="Receipt"
                    style={{ width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'contain' }}
                  />
                )
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {currentTab === 3 && (
        <Box>
          {/* Balance summary */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '4px solid', borderColor: 'success.main', bgcolor: '#f1f8e9' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Total Received</Typography>
                  <Typography variant="h5" color="success.main" fontWeight={700}>
                    {formatCurrency(pettyCashBalance?.totalReceived || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">All time</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '4px solid', borderColor: 'error.main', bgcolor: '#fce4ec' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Total Spent</Typography>
                  <Typography variant="h5" color="error.main" fontWeight={700}>
                    {formatCurrency(pettyCashBalance?.totalSpent || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">All time</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '4px solid', borderColor: (pettyCashBalance?.balance || 0) >= 0 ? 'primary.main' : 'warning.main', bgcolor: (pettyCashBalance?.balance || 0) >= 0 ? '#e3f2fd' : '#fff8e1' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Balance in Hand</Typography>
                  <Typography variant="h5" fontWeight={700} color={(pettyCashBalance?.balance || 0) >= 0 ? 'primary.main' : 'warning.main'}>
                    {formatCurrency(pettyCashBalance?.balance || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Received − Spent</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Per-person balance breakdown */}
          {pettyCashBalance?.byPerson?.length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Balance by Person</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>Received (₹)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>Spent (₹)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Balance (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pettyCashBalance.byPerson.map((p, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(p.received)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(p.spent)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={p.balance >= 0 ? 'primary.main' : 'warning.main'}>
                            {formatCurrency(p.balance)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Fund Receipts */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6" fontWeight={600}>Fund Receipts (Cash In)</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenReceiptDialog()}>
              Add Receipt
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'success.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Received From</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Received By</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Transfer Mode</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reference No.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Remarks</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pcLoading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : pettyCashReceipts.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No fund receipts recorded</TableCell></TableRow>
                ) : (
                  pettyCashReceipts.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>{new Date(r.date + 'T00:00:00').toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{r.receivedFrom}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{r.receivedByName || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                      <TableCell><Chip label={r.transferMode} size="small" color="success" variant="outlined" /></TableCell>
                      <TableCell>{r.referenceNo || '—'}</TableCell>
                      <TableCell align="right"><Typography fontWeight={600} color="success.main">{formatCurrency(r.amount)}</Typography></TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.remarks || '—'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleOpenReceiptDialog(r)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteReceipt2(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Fund Expenses */}
          <Typography variant="h6" fontWeight={600} mb={1.5}>Fund Expenses (Cash Out)</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'error.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Particulars</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Mode</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Vendor</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Petty Cash Holder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pcLoading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : fundExpenses.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No expenses tagged as Petty Cash yet. Check "Paid from Petty Cash Fund" when adding expenses.</TableCell></TableRow>
                ) : (
                  fundExpenses.map(e => (
                    <TableRow key={e.id} hover>
                      <TableCell>{new Date(e.date + 'T00:00:00').toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{e.particulars}</TableCell>
                      <TableCell><Chip label={e.category} size="small" /></TableCell>
                      <TableCell>{e.modeOfTransaction}</TableCell>
                      <TableCell align="right"><Typography fontWeight={600} color="error.main">{formatCurrency(e.amount)}</Typography></TableCell>
                      <TableCell>{e.vendorName || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{e.pettyCashHolderName || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Add/Edit Receipt Dialog */}
      <Dialog open={pcReceiptDialog} onClose={() => setPcReceiptDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingReceipt ? 'Edit Fund Receipt' : 'Record Fund Receipt'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Date" value={pcReceiptForm.date}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Amount" value={pcReceiptForm.amount}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Received From" value={pcReceiptForm.receivedFrom}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, receivedFrom: e.target.value })}
                placeholder="e.g. Finance Dept / Manager Name" required />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Received By (Employee)</InputLabel>
                <Select
                  value={pcReceiptForm.receivedByUserId || ''}
                  label="Received By (Employee)"
                  onChange={e => {
                    const emp = pcEmployees.find(u => u.id === e.target.value);
                    setPcReceiptForm({ ...pcReceiptForm, receivedByUserId: e.target.value, receivedByName: emp?.fullName || '' });
                  }}
                >
                  <MenuItem value=""><em>— Select who received the cash —</em></MenuItem>
                  {pcEmployees.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.fullName}{u.employeeId ? ` (${u.employeeId})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Transfer Mode" value={pcReceiptForm.transferMode}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, transferMode: e.target.value })} required>
                {['Cash', 'GPay', 'PhonePe', 'Paytm', 'UPI', 'Bank Transfer', 'NEFT', 'IMPS'].map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Reference No. (optional)" value={pcReceiptForm.referenceNo}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, referenceNo: e.target.value })}
                placeholder="GPay Txn ID / Bank ref" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Remarks (optional)" value={pcReceiptForm.remarks}
                onChange={e => setPcReceiptForm({ ...pcReceiptForm, remarks: e.target.value })}
                multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPcReceiptDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleSaveReceipt}
            disabled={!pcReceiptForm.date || !pcReceiptForm.amount || !pcReceiptForm.receivedFrom || !pcReceiptForm.transferMode}>
            {editingReceipt ? 'Update' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── FINANCIAL INSIGHTS TAB ─────────────────────────────────────── */}
      {currentTab === 4 && (
        isOwner ? (
        <Box>
          {/* Header row */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <LightbulbIcon color="warning" />
              <Typography variant="h6" fontWeight={700}>Financial Insights</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={insightsYear}
                  label="Year"
                  onChange={e => {
                    setInsightsYear(e.target.value);
                    fetchInsights(e.target.value);
                  }}
                >
                  {[2024, 2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" onClick={() => fetchInsights(insightsYear)}>Refresh</Button>
            </Box>
          </Box>

          {insightsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : !insights ? (
            <Alert severity="info">Select a year to load financial insights.</Alert>
          ) : (() => {
            const COLORS = ['#2196f3','#4caf50','#ff9800','#e91e63','#9c27b0','#00bcd4','#ff5722','#607d8b','#8bc34a','#ffc107'];
            const fmt = v => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
            const monthLabel = m => {
              const [y, mo] = m.split('-');
              return new Date(parseInt(y), parseInt(mo) - 1).toLocaleString('en-IN', { month: 'short' });
            };
            const { summary, monthlyData, expenseByCategory, revenueByCategory, projection } = insights;

            return (
              <Box>
                {/* ── Summary Cards ── */}
                <Grid container spacing={2} mb={3}>
                  {[
                    { label: 'Total Revenue', value: summary.totalRevenue, color: 'success.main', bg: '#f1f8e9', icon: <TrendingUpIcon /> },
                    { label: 'Total Expenses', value: summary.totalExpense, color: 'error.main', bg: '#fff3e0', icon: <TrendingDownIcon /> },
                    { label: 'Net Profit / Loss', value: summary.netProfit, color: summary.netProfit >= 0 ? 'success.main' : 'error.main', bg: summary.netProfit >= 0 ? '#e8f5e9' : '#ffebee', icon: <ShowChartIcon /> },
                    { label: 'Avg Monthly Burn', value: summary.avgMonthlyExpense, color: 'warning.main', bg: '#fff8e1', icon: <MoneyIcon /> }
                  ].map(c => (
                    <Grid item xs={6} md={3} key={c.label}>
                      <Card sx={{ borderLeft: '4px solid', borderColor: c.color, bgcolor: c.bg }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                            <Box sx={{ color: c.color, opacity: 0.7 }}>{c.icon}</Box>
                          </Box>
                          <Typography variant="h6" fontWeight={700} color={c.color}>{fmt(c.value)}</Typography>
                          <Typography variant="caption" color="text.secondary">{insights.year}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}

                  {/* Opening Balance (B/F) */}
                  {summary.openingBalance !== undefined && (
                    <Grid item xs={6} md={3}>
                      <Card sx={{ borderLeft: '4px solid', borderColor: summary.openingBalance >= 0 ? 'success.main' : 'error.main', bgcolor: summary.openingBalance >= 0 ? '#f1f8e9' : '#fce4ec' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">
                            🏦 Opening Balance {summary.openingBalanceLabel ? `(${summary.openingBalanceLabel})` : '(B/F)'}
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color={summary.openingBalance >= 0 ? 'success.main' : 'error.main'}>
                            {summary.openingBalance >= 0 ? '+' : ''}{fmt(summary.openingBalance)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Carried forward into period</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Closing Balance */}
                  {summary.closingBalance !== undefined && (
                    <Grid item xs={6} md={3}>
                      <Card sx={{ borderLeft: '4px solid', borderColor: summary.closingBalance >= 0 ? 'success.main' : 'error.main', bgcolor: summary.closingBalance >= 0 ? '#e3f2fd' : '#fce4ec' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">🏁 Closing Balance (incl. B/F)</Typography>
                          <Typography variant="h6" fontWeight={700} color={summary.closingBalance >= 0 ? '#1565c0' : 'error.main'}>
                            {summary.closingBalance >= 0 ? '+' : ''}{fmt(summary.closingBalance)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            B/F {fmt(summary.openingBalance)} + Net {fmt(summary.netProfit)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                {/* ── Revenue vs Expense Monthly Bar Chart ── */}
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Monthly Revenue vs Expenses — {insights.year}</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <ReTooltip formatter={(val, name) => [fmt(val), name === 'revenue' ? 'Revenue' : name === 'expense' ? 'Expense' : 'Net']} labelFormatter={monthLabel} />
                      <Legend formatter={v => v === 'revenue' ? 'Revenue' : v === 'expense' ? 'Expense' : 'Net'} />
                      <Bar dataKey="revenue" fill="#4caf50" radius={[3,3,0,0]} />
                      <Bar dataKey="expense" fill="#f44336" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* ── Net P&L Line Chart ── */}
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Net Profit / Loss by Month</Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <ReTooltip formatter={val => [fmt(val), 'Net']} labelFormatter={monthLabel} />
                      <ReferenceLine y={0} stroke="#999" strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="net" stroke="#2196f3" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>

                {/* ── Category Breakdown ── */}
                <Grid container spacing={3} mb={3}>
                  {/* Expenses by category */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle1" fontWeight={700} mb={2} color="error.main">Expenses by Category</Typography>
                      {expenseByCategory.length === 0 ? (
                        <Typography color="text.secondary" variant="body2">No expense data for {insights.year}</Typography>
                      ) : (
                        <Box>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={expenseByCategory} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={75} label={false} labelLine={false}>
                                {expenseByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                              </Pie>
                              <ReTooltip formatter={(val, name) => [fmt(val), name]} />
                              <Legend formatter={v => v} wrapperStyle={{ fontSize: '0.7rem' }} />
                            </PieChart>
                          </ResponsiveContainer>
                          <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#fff3e0' }}>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Category</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>% Share</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {expenseByCategory.map((c, i) => (
                                  <TableRow key={c.category} hover>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length], flexShrink: 0 }} />
                                        <Typography variant="caption">{c.category}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right"><Typography variant="caption" fontWeight={600}>{fmt(c.total)}</Typography></TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <Typography variant="caption">{summary.totalExpense > 0 ? ((c.total / summary.totalExpense) * 100).toFixed(1) : 0}%</Typography>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#fff3e0', borderTop: '2px solid #e65100' }}>
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'error.main' }}>{fmt(summary.totalExpense)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>100%</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Revenue by category */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle1" fontWeight={700} mb={2} color="success.main">Revenue by Category</Typography>
                      {revenueByCategory.length === 0 ? (
                        <Typography color="text.secondary" variant="body2">No revenue data for {insights.year}</Typography>
                      ) : (
                        <Box>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={revenueByCategory} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={75} label={false} labelLine={false}>
                                {revenueByCategory.map((_, i) => <Cell key={i} fill={['#4caf50','#8bc34a','#cddc39','#66bb6a','#26a69a','#00bcd4','#43a047'][i % 7]} />)}
                              </Pie>
                              <ReTooltip formatter={(val, name) => [fmt(val), name]} />
                              <Legend formatter={v => v} wrapperStyle={{ fontSize: '0.7rem' }} />
                            </PieChart>
                          </ResponsiveContainer>
                          <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f8e9' }}>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Category</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>% Share</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {revenueByCategory.map((c, i) => (
                                  <TableRow key={c.category} hover>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ['#4caf50','#8bc34a','#cddc39','#66bb6a','#26a69a','#00bcd4','#43a047'][i % 7], flexShrink: 0 }} />
                                        <Typography variant="caption">{c.category}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right"><Typography variant="caption" fontWeight={600}>{fmt(c.total)}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="caption">{summary.totalRevenue > 0 ? ((c.total / summary.totalRevenue) * 100).toFixed(1) : 0}%</Typography></TableCell>
                                  </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#f1f8e9', borderTop: '2px solid #2e7d32' }}>
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'success.main' }}>{fmt(summary.totalRevenue)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>100%</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* ── Monthly Projection ── */}
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ShowChartIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Monthly Projection</Typography>
                    <Chip label={`Based on ${insights.year} actuals`} size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                  </Box>

                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Avg Monthly Revenue</Typography>
                          <Typography variant="h6" fontWeight={700} color="success.main">{fmt(summary.avgMonthlyRevenue)}</Typography>
                          <Typography variant="caption" color="text.secondary">avg over {insights.year} active months</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#fce4ec', border: '1px solid #f48fb1' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Avg Monthly Expense</Typography>
                          <Typography variant="h6" fontWeight={700} color="error.main">{fmt(summary.avgMonthlyExpense)}</Typography>
                          <Typography variant="caption" color="text.secondary">avg over {insights.year} active months</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: summary.avgMonthlyRevenue >= summary.avgMonthlyExpense ? '#e8f5e9' : '#ffebee', border: `1px solid ${summary.avgMonthlyRevenue >= summary.avgMonthlyExpense ? '#a5d6a7' : '#ef9a9a'}` }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Projected Net / Month</Typography>
                          <Typography variant="h6" fontWeight={700} color={summary.avgMonthlyRevenue >= summary.avgMonthlyExpense ? 'success.main' : 'error.main'}>
                            {fmt(summary.avgMonthlyRevenue - summary.avgMonthlyExpense)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">revenue − expense</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Salary Cost / Month</Typography>
                          <Typography variant="h6" fontWeight={700} color="primary.main">{fmt(projection.salaryProjection)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {projection.salaryProjectionSource === 'master'
                              ? `${projection.activeEmployeeCount} active employees (salary master)`
                              : projection.salaryProjectionSource === 'actuals'
                              ? 'avg of last 3 months salary payments'
                              : 'No salary data found'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Month-by-month table */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.secondary">
                    Month-by-Month Actuals — {insights.year}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>Revenue</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>Expense</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Net</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '25%' }}>Revenue vs Expense</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const maxVal = Math.max(...monthlyData.map(m => Math.max(m.revenue, m.expense)), 1);
                          const totals = monthlyData.reduce((a, m) => ({ revenue: a.revenue + m.revenue, expense: a.expense + m.expense, net: a.net + m.net }), { revenue: 0, expense: 0, net: 0 });
                          return [
                            ...monthlyData.map(m => {
                              const [, mo] = m.month.split('-');
                              const label = new Date(parseInt(m.month.split('-')[0]), parseInt(mo) - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
                              const hasData = m.revenue > 0 || m.expense > 0;
                              return (
                                <TableRow key={m.month} hover sx={{ opacity: hasData ? 1 : 0.4 }}>
                                  <TableCell><Typography variant="body2" fontWeight={500}>{label}</Typography></TableCell>
                                  <TableCell align="right"><Typography variant="body2" color="success.main" fontWeight={600}>{m.revenue > 0 ? fmt(m.revenue) : '—'}</Typography></TableCell>
                                  <TableCell align="right"><Typography variant="body2" color="error.main" fontWeight={600}>{m.expense > 0 ? fmt(m.expense) : '—'}</Typography></TableCell>
                                  <TableCell align="right">
                                    {hasData && <Typography variant="body2" fontWeight={700} color={m.net >= 0 ? 'success.main' : 'error.main'}>{m.net >= 0 ? '+' : ''}{fmt(m.net)}</Typography>}
                                  </TableCell>
                                  <TableCell>
                                    {hasData && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                                          <Box sx={{ flex: 1, height: 4, bgcolor: '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                            <Box sx={{ width: `${(m.revenue / maxVal) * 100}%`, height: '100%', bgcolor: '#4caf50', borderRadius: 2 }} />
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
                                          <Box sx={{ flex: 1, height: 4, bgcolor: '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                            <Box sx={{ width: `${(m.expense / maxVal) * 100}%`, height: '100%', bgcolor: '#f44336', borderRadius: 2 }} />
                                          </Box>
                                        </Box>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            }),
                            <TableRow key="total" sx={{ bgcolor: '#f5f5f5', fontWeight: 700 }}>
                              <TableCell><Typography variant="body2" fontWeight={700}>Total {insights.year}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={700} color="success.main">{fmt(totals.revenue)}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={700} color="error.main">{fmt(totals.expense)}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={700} color={totals.net >= 0 ? 'success.main' : 'error.main'}>{totals.net >= 0 ? '+' : ''}{fmt(totals.net)}</Typography></TableCell>
                              <TableCell />
                            </TableRow>
                          ];
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* ── Salary Actual vs Projected (last 3 months) ── */}
                {Object.keys(projection.salaryActualsByMonth).length > 0 && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PeopleIcon color="secondary" />
                      <Typography variant="subtitle1" fontWeight={700}>Salary: Actual vs Projected (Last 3 Months)</Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actual Paid</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Salary Master</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(projection.salaryActualsByMonth).map(([month, actual]) => {
                            const variance = actual - projection.salaryProjection;
                            return (
                              <TableRow key={month} hover>
                                <TableCell><Typography variant="body2" fontWeight={500}>{month}</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(actual)}</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2" color="text.secondary">{fmt(projection.salaryProjection)}</Typography></TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600} color={variance <= 0 ? 'success.main' : 'error.main'}>
                                    {variance <= 0 ? '▼' : '▲'} {fmt(Math.abs(variance))}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            );
          })()}
        </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" py={10}>
            <Alert severity="warning">Financial Insights is restricted to Owners only.</Alert>
          </Box>
        )
      )}

      {/* ── New Month Recurring Banner ── */}
      {newMonthBanner && (
        <Box sx={{ position: 'fixed', bottom: 80, right: 24, zIndex: 1400, maxWidth: 360 }}>
          <Paper elevation={6} sx={{ p: 2, borderLeft: '4px solid #9c27b0', bgcolor: '#f3e5f5' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <RepeatIcon color="secondary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} color="secondary">Recurring Expenses</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {recurringPreview.filter(i => !i.alreadyExists).length} recurring expense(s) not yet added for this month.
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="contained" color="secondary" onClick={handleOpenRecurringPreview}>Review & Add</Button>
              <Button size="small" variant="text" onClick={() => setNewMonthBanner(false)}>Dismiss</Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── Recurring Preview / Generate Dialog ── */}
      <Dialog open={recurringPreviewOpen} onClose={() => setRecurringPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutorenewIcon color="secondary" /> Generate Recurring Expenses
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The following will be created with <strong>Pending</strong> status. Already-existing entries are greyed out.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recurringPreview.map((item, i) => (
                <TableRow key={i} sx={{ opacity: item.alreadyExists ? 0.4 : 1 }}>
                  <TableCell>
                    <Typography variant="caption">{item.title}</Typography>
                    {item.vendor && <Typography variant="caption" display="block" color="text.secondary">{item.vendor}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption">{item.amount > 0 ? `₹${Number(item.amount).toLocaleString('en-IN')}` : '—'}</Typography></TableCell>
                  <TableCell>
                    {item.alreadyExists
                      ? <Chip label="Exists" size="small" sx={{ fontSize: '0.65rem' }} />
                      : <Chip label="Will Add" size="small" color="secondary" sx={{ fontSize: '0.65rem' }} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringPreviewOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="secondary"
            onClick={handleGenerateRecurring}
            disabled={recurringGenerating || recurringPreview.filter(i => !i.alreadyExists).length === 0}
            startIcon={recurringGenerating ? <CircularProgress size={16} /> : <AutorenewIcon />}
          >
            {recurringGenerating ? 'Adding...' : `Add ${recurringPreview.filter(i => !i.alreadyExists).length} Expense(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Recurring Templates Manager Dialog ── */}
      <Dialog open={recurringDialogOpen} onClose={() => setRecurringDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1}><RepeatIcon color="secondary" /> Recurring Expenses</Box>
          <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" color="secondary" startIcon={<AutorenewIcon />} onClick={handleOpenRecurringPreview}>
              Generate for This Month
            </Button>
            <Button size="small" variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setRecurringTemplateForm({ title: '', vendor: '', amount: '', category: '', paymentMode: 'G-Pay', transactionType: 'Debit', monthNaming: 'none' })}>
              Add Template
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Month Suffix</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {recurringTemplates.map(tpl => (
                <TableRow key={tpl.id} hover sx={{ opacity: tpl.isActive ? 1 : 0.45 }}>
                  <TableCell><Typography variant="caption" fontWeight={600}>{tpl.title}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{tpl.vendor || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{tpl.amount > 0 ? `₹${Number(tpl.amount).toLocaleString('en-IN')}` : '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{tpl.category || '—'}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" sx={{ fontSize: '0.65rem' }}
                      label={tpl.monthNaming === 'previous' ? 'Prev Month' : tpl.monthNaming === 'current' ? 'Curr Month' : 'None'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={tpl.isActive ? 'Active' : 'Off'} color={tpl.isActive ? 'success' : 'default'} sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" onClick={() => setRecurringTemplateForm({ ...tpl })}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(tpl.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Template Form Dialog ── */}
      {recurringTemplateForm && (
        <Dialog open={!!recurringTemplateForm} onClose={() => setRecurringTemplateForm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{recurringTemplateForm.id ? 'Edit Template' : 'Add Recurring Template'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Title" size="small" fullWidth value={recurringTemplateForm.title || ''} onChange={e => setRecurringTemplateForm(p => ({ ...p, title: e.target.value }))} />
            <TextField label="Vendor" size="small" fullWidth value={recurringTemplateForm.vendor || ''} onChange={e => setRecurringTemplateForm(p => ({ ...p, vendor: e.target.value }))} />
            <TextField label="Amount (0 = TBD)" size="small" fullWidth type="number" value={recurringTemplateForm.amount || ''} onChange={e => setRecurringTemplateForm(p => ({ ...p, amount: e.target.value }))} />
            <TextField label="Category" size="small" fullWidth value={recurringTemplateForm.category || ''} onChange={e => setRecurringTemplateForm(p => ({ ...p, category: e.target.value }))} />
            <TextField label="Payment Mode" size="small" fullWidth value={recurringTemplateForm.paymentMode || ''} onChange={e => setRecurringTemplateForm(p => ({ ...p, paymentMode: e.target.value }))} />
            <FormControl size="small" fullWidth>
              <InputLabel>Month Suffix in Title</InputLabel>
              <Select label="Month Suffix in Title" value={recurringTemplateForm.monthNaming || 'none'} onChange={e => setRecurringTemplateForm(p => ({ ...p, monthNaming: e.target.value }))}>
                <MenuItem value="none">None — title as-is</MenuItem>
                <MenuItem value="current">Current Month (e.g. May 2026)</MenuItem>
                <MenuItem value="previous">Previous Month (e.g. Apr 2026) — use for Rent</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Active</InputLabel>
              <Select label="Active" value={recurringTemplateForm.isActive !== false ? true : false} onChange={e => setRecurringTemplateForm(p => ({ ...p, isActive: e.target.value }))}>
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecurringTemplateForm(null)}>Cancel</Button>
            <Button variant="contained" color="secondary" onClick={() => handleSaveTemplate(recurringTemplateForm)}
              disabled={!recurringTemplateForm.title}>Save</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ExpenseManagement;
